import { ChevronDown, Equal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';

import type { ProductTimelineEntry } from '../../types/meta-delivery.types';
import { diffPayloads, type PayloadFieldChange } from '../../utils/payload-diff';

const CHANGE_STYLE: Record<PayloadFieldChange, string> = {
  changed: 'bg-warning-50',
  added: 'bg-success-50',
  removed: 'bg-error-50',
  unchanged: '',
};

interface Props {
  entry: ProductTimelineEntry;
  /** The product's delivery just before this one (older job), when known. */
  previous: ProductTimelineEntry;
}

/**
 * How this delivery's payload relates to the previous one. Equal
 * `payloadHash`es mean byte-identical payloads, said in one line. Otherwise,
 * when both payloads were captured, a toggle opens a field-by-field diff
 * (changed / added / removed first); when they weren't, it says so rather
 * than guessing.
 */
export function PayloadComparison({ entry, previous }: Props) {
  const { t } = useTranslation('deliveries');
  const [open, setOpen] = useState(false);
  const shortJob = previous.deliveryJobId.slice(0, 8);

  if (entry.payloadHash && entry.payloadHash === previous.payloadHash) {
    return (
      <p
        data-testid={`payload-comparison-identical-${entry.deliveryJobId}`}
        className="text-muted-foreground mt-3 flex items-center gap-1.5 text-xs"
      >
        <Equal aria-hidden="true" className="size-3.5" />
        {t('productLog.payload.identical', { job: shortJob })}
      </p>
    );
  }

  const before = previous.transformedData;
  const after = entry.transformedData;
  if (!before || !after) {
    return (
      <p
        data-testid={`payload-comparison-unavailable-${entry.deliveryJobId}`}
        className="text-muted-foreground mt-3 text-xs"
      >
        {t('productLog.payload.notComparable', { job: shortJob })}
      </p>
    );
  }

  const rows = diffPayloads(before, after);
  const changed = rows.filter((r) => r.change !== 'unchanged');

  return (
    <div data-testid={`payload-comparison-${entry.deliveryJobId}`} className="mt-3">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-2 h-7 cursor-pointer gap-1 px-2 text-xs"
        aria-expanded={open}
        data-testid={`payload-comparison-toggle-${entry.deliveryJobId}`}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <ChevronDown
          aria-hidden="true"
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
        />
        {t('productLog.payload.compare', { count: changed.length, job: shortJob })}
      </Button>
      {open ? (
        changed.length === 0 ? (
          <p className="text-muted-foreground mt-1 text-xs">
            {t('productLog.payload.noFieldChanges')}
          </p>
        ) : (
          <div className="mt-1 rounded-lg border">
            <Table className="text-xs">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="h-8 px-3 text-xs">
                    {t('productLog.payload.field')}
                  </TableHead>
                  <TableHead className="h-8 px-3 text-xs">
                    {t('productLog.payload.previous', { job: shortJob })}
                  </TableHead>
                  <TableHead className="h-8 px-3 text-xs">
                    {t('productLog.payload.thisJob')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changed.map((row) => (
                  <TableRow key={row.field} className={cn('align-top', CHANGE_STYLE[row.change])}>
                    <TableCell className="px-3 py-1.5 font-mono whitespace-nowrap">
                      {row.field}
                      <span className="text-muted-foreground ml-1.5 font-sans">
                        {t(`productLog.payload.change.${row.change}`)}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground px-3 py-1.5 break-words">
                      {row.before ?? '—'}
                    </TableCell>
                    <TableCell className="text-foreground px-3 py-1.5 break-words">
                      {row.after ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : null}
    </div>
  );
}
